import {
  Component,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  computed,
  signal,
  ViewChild,
  OnInit,
  OnDestroy,
  inject,
  input
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { TableComponent } from '@view/shared/molecules/app-table/app-table.component';
import { PaginatorComponent } from '@view/shared/molecules/paginator/paginator.component';
import { ButtonComponent } from '@view/shared/molecules/button/button.component';
import {
  ButtonTypeEnum,
  ButtonSizeEnum
} from '@view/shared/molecules/button/buttonTypesEnum';
import { TableActionType } from '@view/shared/molecules/app-table/tableActionTypes';
import { ToastService } from '@view/shared/molecules/toast/service/toast.service';
import { ToastTypeEnum } from '@view/shared/molecules/toast/toast-type-enum';
import {
  ObservationsPresenter,
  ObservationModuleType
} from '@view/preclinical/presenters/observations.presenter';
import { ObservationListItemDTO } from '@infra-services/observations/api/observations-api.interface';
import {
  ColumnOption,
  FrozenColumnsSidebarComponent
} from '@view/preclinical/molecules/frozen-columns-sidebar/frozen-columns-sidebar.component';
import { FrozenColumnsService } from '@helpers/table/frozen-columns.service';
import { ViewExpansionStateService } from '@helpers/state/view-expansion-state.service';
import { ObservationEditModalComponent } from '../observation-edit-modal/observation-edit-modal.component';
import { ObservationHistoryModalComponent } from '../observation-history-modal/observation-history-modal.component';
import {
  SearchControlsComponent,
  SearchEvent,
  SearchFieldOption
} from '@view/shared/molecules/search-controls/search-controls.component';
import { FilterSidebarComponent } from '@view/preclinical/molecules/filter-sidebar/filter-sidebar.component';
import { ActiveFiltersComponent } from '@view/shared/molecules/active-filters/active-filters.component';
import {
  ExportConfirmationModalComponent,
  ExportModalState
} from '@view/preclinical/molecules/export-confirmation-modal/export-confirmation-modal.component';
import {
  MetricsDashboardComponent,
  MetricItem
} from '@view/shared/molecules/metrics-dashboard/metrics-dashboard.component';
import { OBSERVATION_SEARCH_OPTIONS } from '@view/preclinical/config/search-options.config';
import { FilterCriteria } from '@model/filter/filter-options.model';
import { SearchField } from '../../../../../domain/types/search-field.type';
import { ObservationMetrics } from '@model/observation/observation-metrics.model';

@Component({
  selector: 'app-observations-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableComponent,
    PaginatorComponent,
    ButtonComponent,
    FrozenColumnsSidebarComponent,
    ObservationEditModalComponent,
    ObservationHistoryModalComponent,
    SearchControlsComponent,
    FilterSidebarComponent,
    ActiveFiltersComponent,
    ExportConfirmationModalComponent,
    MetricsDashboardComponent
  ],
  templateUrl: './observations-list.component.html',
  styleUrls: ['./observations-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    FrozenColumnsService,
    {
      provide: 'FROZEN_COLUMNS_STORAGE_KEY',
      useValue: 'sig-frozen-columns-observations'
    }
  ]
})
export class ObservationsListComponent implements OnInit, OnDestroy {
  readonly moduleType = input<ObservationModuleType>('preclinical');

  @ViewChild(PaginatorComponent) paginator: PaginatorComponent | undefined;

  private readonly _observations = signal<ObservationListItemDTO[]>([]);
  readonly observations = this._observations.asReadonly();

  private readonly _currentPage = signal(0);
  readonly currentPage = this._currentPage.asReadonly();

  private readonly _pageSize = signal(10);
  readonly pageSize = this._pageSize.asReadonly();

  private readonly _totalRecords = signal(0);
  readonly totalRecords = this._totalRecords.asReadonly();

  private readonly _isLoading = signal(false);
  readonly isLoading = this._isLoading.asReadonly();

  private readonly _activeTab = signal<ObservationModuleType>('preclinical');
  readonly activeTab = this._activeTab.asReadonly();

  private readonly _dashboardMetrics = signal<MetricItem[]>([]);
  readonly dashboardMetrics = this._dashboardMetrics.asReadonly();
  private metricsSubscription?: Subscription;

  private dataSubscription?: Subscription;

  readonly ButtonTypeEnum = ButtonTypeEnum;
  readonly ButtonSizeEnum = ButtonSizeEnum;
  readonly ExportModalState = ExportModalState;

  protected readonly viewExpansionState = inject(ViewExpansionStateService);

  isFrozenSidebarOpen = false;
  isFilterSidebarOpen = false;

  private readonly _showSearchControls = signal(false);
  readonly showSearchControls = this._showSearchControls.asReadonly();
  readonly searchFieldOptions: SearchFieldOption[] = OBSERVATION_SEARCH_OPTIONS;
  private currentSearchField: SearchField | string | null = null;
  private currentSearchValue: string | null = null;

  currentFilterCriteria: FilterCriteria | null = null;

  private readonly _isExportModalOpen = signal(false);
  readonly isExportModalOpen = this._isExportModalOpen.asReadonly();
  private readonly _exportModalState = signal<ExportModalState>(
    ExportModalState.CONFIRMATION
  );
  readonly exportModalState = this._exportModalState.asReadonly();

  private readonly _isEditModalOpen = signal(false);
  readonly isEditModalOpen = this._isEditModalOpen.asReadonly();

  private readonly _isHistoryModalOpen = signal(false);
  readonly isHistoryModalOpen = this._isHistoryModalOpen.asReadonly();

  private readonly _selectedObservation = signal<ObservationListItemDTO | null>(
    null
  );
  readonly selectedObservation = this._selectedObservation.asReadonly();

  get isExpanded(): boolean {
    return this.viewExpansionState.isExpanded();
  }

  get hasActiveFilters(): boolean {
    return this.currentFilterCriteria !== null;
  }

  private readonly ICON_HEXAGONO = 'icon-hexagono-exclamacion';
  private readonly ICON_RECIBO = 'icon-recibo';
  private readonly ICON_CARNET = 'icon-carnet';
  private readonly ICON_FICHA_NOMBRE = 'icon-ficha-nombre';
  private readonly ICON_CALENDARIO = 'icon-calendar';
  private readonly ICON_CLIPBOARD = 'icon-clipboard';
  private readonly ICON_OBSERVACION = 'icon-observacion';
  private readonly ICON_PRIORIDAD = 'icon-prioridad';
  private readonly ICON_EXCLAMACION = 'icon-hexagono-exclamacion-rojo';
  private readonly ICON_ESTADO = 'icon-estado';

  private readonly ALL_COLUMNS = [
    {
      key: 'reprocessStatus',
      label: 'Estado',
      searchable: false,
      icon: this.ICON_CLIPBOARD,
      type: 'status' as const
    },
    {
      key: 'isReprocess',
      label: 'Reproceso',
      searchable: false,
      icon: this.ICON_HEXAGONO,
      type: 'boolean-icon' as const,
      booleanIcon: this.ICON_EXCLAMACION
    },
    {
      key: 'reprocessCause',
      label: 'Causa reproceso',
      searchable: false,
      icon: this.ICON_HEXAGONO
    },
    {
      key: 'reprocessObservation',
      label: 'Observación',
      searchable: false,
      icon: this.ICON_OBSERVACION
    },
    {
      key: 'priority',
      label: 'Prioridad',
      searchable: false,
      icon: this.ICON_PRIORIDAD,
      type: 'priority' as const
    },
    {
      key: 'examName',
      label: 'Tipo examen',
      searchable: false,
      icon: this.ICON_CLIPBOARD
    },
    {
      key: 'acmg',
      label: 'ACMG VAL',
      searchable: false,
      icon: this.ICON_HEXAGONO,
      type: 'boolean-text' as const
    },
    {
      key: 'lgNumber',
      label: 'Código LG',
      searchable: false,
      icon: this.ICON_RECIBO
    },
    {
      key: 'caseNumber',
      label: 'Código de muestra',
      searchable: true,
      icon: this.ICON_RECIBO
    },
    {
      key: 'orderNumber',
      label: 'PATCORE',
      searchable: false,
      icon: this.ICON_RECIBO
    },
    {
      key: 'admissionDate',
      label: 'Fecha ingreso',
      searchable: false,
      icon: this.ICON_CALENDARIO,
      type: 'date' as const,
      tooltip: {
        pipe: 'fullDateTime' as const,
        alwaysShow: true
      }
    },
    {
      key: 'identificationType',
      label: 'Tipo ID',
      searchable: false,
      icon: this.ICON_CARNET
    },
    {
      key: 'identificationNumber',
      label: 'ID Paciente',
      searchable: true,
      icon: this.ICON_CARNET
    },
    {
      key: 'patientFirstName',
      label: 'Nombres',
      searchable: true,
      icon: this.ICON_FICHA_NOMBRE
    },
    {
      key: 'patientLastName',
      label: 'Apellidos',
      searchable: true,
      icon: this.ICON_FICHA_NOMBRE
    },
    {
      key: 'birthDate',
      label: 'Fecha nacimiento',
      searchable: false,
      icon: this.ICON_CALENDARIO,
      type: 'date' as const
    },
    {
      key: 'examCode',
      label: 'Código examen',
      searchable: false,
      icon: this.ICON_CLIPBOARD
    },
    {
      key: 'actions',
      label: 'Acciones',
      searchable: false,
      icon: 'icon-plus-circle'
    }
  ];

  // Columns to exclude per module (these columns don't exist in certain module's data model)
  private readonly EXCLUDED_COLUMNS_BY_MODULE: Record<
    ObservationModuleType,
    string[]
  > = {
    preclinical: [],
    laboratory: ['acmg'] // ACMG doesn't exist in laboratory data model
  };

  private readonly ALL_COLUMN_KEYS = this.ALL_COLUMNS.filter(
    (col) => col.key !== 'actions'
  ).map((col) => col.key);

  readonly availableColumns: ColumnOption[] = this.ALL_COLUMNS.filter(
    (col) => col.key !== 'actions'
  ).map((col) => ({
    key: col.key,
    label: col.label
  }));

  readonly frozenColumnsInOrder = computed(() => {
    return this.frozenColumnsService.getFrozenColumnsInOrder(
      this.ALL_COLUMN_KEYS
    );
  });

  readonly visibleColumnsComputed = computed(() => {
    const frozenInOrder = this.frozenColumnsService.getFrozenColumnsInOrder(
      this.ALL_COLUMN_KEYS
    );

    const nonFrozenInOrder =
      this.frozenColumnsService.getNonFrozenColumnsInOrder(
        this.ALL_COLUMN_KEYS
      );

    const orderedKeys = [...frozenInOrder, ...nonFrozenInOrder];

    // Get columns to exclude based on current module
    const excludedColumns =
      this.EXCLUDED_COLUMNS_BY_MODULE[this._activeTab()] || [];

    return orderedKeys
      .filter((key) => !excludedColumns.includes(key))
      .map((key) => this.ALL_COLUMNS.find((col) => col.key === key))
      .filter((col): col is (typeof this.ALL_COLUMNS)[0] => col !== undefined);
  });

  tableActions: TableActionType[] = [];

  constructor(
    private readonly presenter: ObservationsPresenter,
    public readonly frozenColumnsService: FrozenColumnsService,
    private readonly cdr: ChangeDetectorRef,
    private readonly toastService: ToastService,
    private readonly route: ActivatedRoute
  ) {
    this.setupTableActions();
  }

  ngOnInit(): void {
    // Check for module query param to auto-switch tab
    const moduleParam = this.route.snapshot.queryParamMap.get('module');
    if (this.presenter.isValidModule(moduleParam)) {
      this._activeTab.set(moduleParam);
      this.presenter.setModule(moduleParam);
    }

    this.loadObservations();
    this.subscribeToMetrics();
  }

  ngOnDestroy(): void {
    this.dataSubscription?.unsubscribe();
    this.metricsSubscription?.unsubscribe();
  }

  /**
   * Subscribe to metrics stream for both modules.
   */
  private subscribeToMetrics(): void {
    this.metricsSubscription?.unsubscribe();
    const metricsParams = this.buildMetricsParams();
    this.metricsSubscription = this.presenter
      .getMetricsStream(metricsParams, 60000)
      .subscribe((metrics) => {
        if (metrics) {
          this._dashboardMetrics.set(this.mapMetricsToItems(metrics));
          this.cdr.markForCheck();
        }
      });
  }

  /**
   * Build metrics params from current filters.
   */
  private buildMetricsParams() {
    if (!this.currentFilterCriteria) {
      return undefined;
    }
    return {
      causeIds: this.currentFilterCriteria.reprocessCauses?.map((c) => c.id),
      startDate: this.currentFilterCriteria.startDate
        ? this.formatDate(this.currentFilterCriteria.startDate)
        : undefined,
      endDate: this.currentFilterCriteria.endDate
        ? this.formatDate(this.currentFilterCriteria.endDate)
        : undefined
    };
  }

  /**
   * Map ObservationMetrics to MetricItem[] for the dashboard.
   */
  private mapMetricsToItems(metrics: ObservationMetrics): MetricItem[] {
    return [
      { label: 'Hoy', value: 'Hoy', isDate: true },
      { label: 'Total general', value: metrics.totalGeneral },
      { label: 'Total del día', value: metrics.totalDelDia },
      { label: 'Reportado', value: metrics.reportado },
      { label: 'En gestión', value: metrics.enGestion },
      { label: 'Solucionado', value: metrics.solucionado }
    ];
  }

  /**
   * Format date for display in metrics dashboard.
   */
  private formatMetricsDate(dateStr: string): string {
    if (!dateStr) {
      return '';
    }
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-CO', {
      day: '2-digit',
      month: '2-digit'
    });
  }

  private setupTableActions(): void {
    this.tableActions = [
      {
        icon: 'icon-editar',
        tooltip: 'Editar estado',
        isHidden: (item: ObservationListItemDTO) => !item.active,
        function: (observationIdOrObject: ObservationListItemDTO | string) => {
          if (typeof observationIdOrObject === 'string') {
            const observation = this.observations().find(
              (o) => o.observationId === observationIdOrObject
            );
            if (observation) {
              this.openEditModal(observation);
            }
          } else {
            this.openEditModal(observationIdOrObject);
          }
        },
        functionProperty: 'observationId'
      },
      {
        icon: 'icon-documento',
        tooltip: 'Ver historial',
        function: (observationIdOrObject: ObservationListItemDTO | string) => {
          if (typeof observationIdOrObject === 'string') {
            const observation = this.observations().find(
              (o) => o.observationId === observationIdOrObject
            );
            if (observation) {
              this.openHistoryModal(observation);
            }
          } else {
            this.openHistoryModal(observationIdOrObject);
          }
        },
        functionProperty: 'observationId'
      }
    ];
  }

  loadObservations(): void {
    this.dataSubscription?.unsubscribe();
    this._isLoading.set(true);

    const queryParams = this.buildQueryParams();

    this.dataSubscription = this.presenter
      .getObservations(queryParams)
      .subscribe({
        next: (result) => {
          this._observations.set(result.observations);
          this._totalRecords.set(result.pagination.total);
          this._isLoading.set(false);
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error('Error loading observations:', error);
          this._observations.set([]);
          this._totalRecords.set(0);
          this._isLoading.set(false);
          this.cdr.detectChanges();
          this.toastService.setToast({
            title: 'Error',
            message: 'Error al cargar las observaciones',
            type: ToastTypeEnum.ERROR
          });
        }
      });
  }

  onPageChange(event: { page: number; pageSize: number }): void {
    this._currentPage.set(event.page);
    this._pageSize.set(event.pageSize);
    this.loadObservations();
  }

  toggleFrozenColumnsSidebar(): void {
    this.isFrozenSidebarOpen = !this.isFrozenSidebarOpen;
  }

  closeFrozenColumnsSidebar(): void {
    this.isFrozenSidebarOpen = false;
  }

  toggleExpansion(): void {
    this.viewExpansionState.toggle();
  }

  openEditModal(observation: ObservationListItemDTO): void {
    this._selectedObservation.set(observation);
    this._isEditModalOpen.set(true);
  }

  closeEditModal(): void {
    this._isEditModalOpen.set(false);
    this._selectedObservation.set(null);
  }

  onStatusUpdated(): void {
    this.closeEditModal();
    this.loadObservations();
    this.toastService.setToast({
      title: 'Éxito',
      message: 'Estado actualizado correctamente',
      type: ToastTypeEnum.SUCCESS
    });
  }

  openHistoryModal(observation: ObservationListItemDTO): void {
    this._selectedObservation.set(observation);
    this._isHistoryModalOpen.set(true);
  }

  closeHistoryModal(): void {
    this._isHistoryModalOpen.set(false);
    this._selectedObservation.set(null);
  }

  onHistoryAdded(): void {
    this.toastService.setToast({
      title: 'Éxito',
      message: 'Observación agregada correctamente',
      type: ToastTypeEnum.SUCCESS
    });
  }

  getModuleTitle(): string {
    return this._activeTab() === 'preclinical' ? 'Preanalítica' : 'Laboratorio';
  }

  onTabChange(tab: ObservationModuleType): void {
    if (this._activeTab() === tab) {
      return;
    }

    this._activeTab.set(tab);
    this._currentPage.set(0);

    // Limpiar datos y mostrar loading al cambiar de tab
    this._observations.set([]);
    this._totalRecords.set(0);

    // Limpiar filtros/búsqueda
    this.currentSearchField = null;
    this.currentSearchValue = null;
    this.currentFilterCriteria = null;

    // Cambiar el backend
    this.presenter.setModule(tab);

    // Cargar datos del nuevo backend
    this.loadObservations();

    this.subscribeToMetrics();
  }

  isTabActive(tab: ObservationModuleType): boolean {
    return this._activeTab() === tab;
  }

  // Search methods
  toggleSearchControls(): void {
    this._showSearchControls.update((value) => !value);
  }

  handleSearch(event: SearchEvent): void {
    this.currentSearchField = event.searchField;
    this.currentSearchValue = event.searchValue;
    this.resetToFirstPageAndReload();
  }

  handleClearSearch(): void {
    this.currentSearchField = null;
    this.currentSearchValue = null;
    this.resetToFirstPageAndReload();
  }

  // Filter methods
  toggleFilterSidebar(): void {
    this.isFilterSidebarOpen = !this.isFilterSidebarOpen;
  }

  closeFilterSidebar(): void {
    this.isFilterSidebarOpen = false;
  }

  handleApplyFilters(criteria: FilterCriteria): void {
    this.currentFilterCriteria = criteria;
    this.resetToFirstPageAndReload();
    this.subscribeToMetrics();
  }

  clearFilters(): void {
    this.currentFilterCriteria = null;
    this.resetToFirstPageAndReload();
    this.subscribeToMetrics();
  }

  getFilterOptionsService() {
    return {
      getFilterOptions: () => this.presenter.getFilterOptions()
    };
  }

  // Export methods
  openExportModal(): void {
    this._exportModalState.set(ExportModalState.CONFIRMATION);
    this._isExportModalOpen.set(true);
  }

  closeExportModal(): void {
    this._isExportModalOpen.set(false);
    setTimeout(() => {
      this._exportModalState.set(ExportModalState.CONFIRMATION);
    }, 300);
  }

  handleExportConfirm(): void {
    this._exportModalState.set(ExportModalState.DOWNLOADING);

    const queryParams = this.buildQueryParams();

    this.presenter.exportObservations(queryParams).subscribe({
      next: (blob) => {
        this.downloadCsvFile(blob);
        this.closeExportModal();
        this.toastService.setToast({
          title: 'Éxito',
          message: 'Exportación completada correctamente',
          type: ToastTypeEnum.SUCCESS
        });
      },
      error: (error) => {
        console.error('Error exporting observations:', error);
        this.closeExportModal();
        this.toastService.setToast({
          title: 'Error',
          message: 'Error al exportar las observaciones',
          type: ToastTypeEnum.ERROR
        });
      }
    });
  }

  private downloadCsvFile(blob: Blob): void {
    const url = globalThis.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `observaciones_${this._activeTab()}_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    globalThis.URL.revokeObjectURL(url);
  }

  private resetToFirstPageAndReload(): void {
    if (this.paginator) {
      this.paginator.specificPage(0);
    } else {
      this._currentPage.set(0);
      this.loadObservations();
    }
  }

  private buildQueryParams() {
    const params: {
      page: number;
      size: number;
      sortBy?: string;
      sortDirection?: string;
      searchField?: string;
      searchValue?: string;
      statusIds?: string[];
      causeIds?: string[];
      startDate?: string;
      endDate?: string;
    } = {
      page: this.currentPage(),
      size: this.pageSize(),
      sortBy: 'observationCreatedAt',
      sortDirection: 'DESC'
    };

    // Add search params
    if (this.currentSearchField && this.currentSearchValue) {
      params.searchField = this.currentSearchField;
      params.searchValue = this.currentSearchValue;
    }

    // Add filter params
    if (this.currentFilterCriteria) {
      if (this.currentFilterCriteria.sampleStatus?.length) {
        params.statusIds = this.currentFilterCriteria.sampleStatus.map(
          (s) => s.code
        );
      }
      if (this.currentFilterCriteria.reprocessCauses?.length) {
        params.causeIds = this.currentFilterCriteria.reprocessCauses.map(
          (c) => c.id
        );
      }
      if (this.currentFilterCriteria.startDate) {
        params.startDate = this.formatDate(
          this.currentFilterCriteria.startDate
        );
      }
      if (this.currentFilterCriteria.endDate) {
        params.endDate = this.formatDate(this.currentFilterCriteria.endDate);
      }
    }

    return params;
  }

  private formatDate(date: Date): string {
    return date.toISOString().split('T')[0];
  }
}
